/* eslint-disable no-console */
/* eslint-disable max-len */
import { makeStyles, CircularProgress, Theme, Table, TableHead, TableRow, TableCell, TableBody } from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { capitalizeAll, GenericMessageBox, GenericPanelComp, useModePanelStyle, useModeTableStyle } from '@moderepo/mode-app-components';
import { SortOrder } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import moment from 'moment';
import 'moment-duration-format';
import React, { useCallback, useMemo } from 'react';
import { MOBILITY_DEFAULT_VEHICLE_ASSIGNMENT_EVENT_METRICS_DEFINITION_ID, useFetchTripsInfoData, MOBILITY_DEFAULT_TRIP_EVENT_METRICS_DEFINITION_ID,
    TripInfoDataSettings, isTripInfoDataSettings, useFetchTripsVehicleAndDriverEntities, TripInfo } from '.';
import { useConvertTimeControlParamsToFetchDataTimeRangeParams } from '../..';
import { EntityUIComponentCollection, EntityUIComponentProps, useSelectTranslatedEntityClasses } from '../../..';
import { useRitzTranslation } from '../../../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../../../utils/logger';
import { RitzComponentsCollection } from '../../../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingNumberValue, isValidSettingStringValue } from '../../../../../utils/utils';
import { getDateTimeDisplay } from '../../../../containerUtils';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        panelHeader: {
            padding: theme.spacing(1),
        },

        panelContent: {
            overflow: 'auto',
            padding : 0,
        },

        tableBodyRow: {
            cursor: 'pointer',
        },

        tableCol: {
            '&.entity-name': {
            },
            '&.timestamp': {
            },
            '&.duration': {
                textAlign: 'center',
            },
            '&.empty': {
                width: '100%',
            },
        },

        [theme.breakpoints.up('md')]: {
            panelHeader: {
                padding: theme.spacing(2),
            },
        },
    };
}, { name: 'EntityTripsTableUIComponent', index: 1 });



const DEFAULT_DURATION_FORMAT = Object.freeze({
    format : 'hh:mm',
    display: 'mobility.duration_format_display',
});



interface LocalizedTripInfo extends TripInfo {
    readonly driverName: string;
    readonly vehicleName: string;
    readonly displayDuration: string;
    readonly displayStartTime: string;
    readonly displayEndTime: string;
}


export interface EntityTripsTableUIComponentCustomSettings {

    // Custom title and icon
    readonly titleKey?: string | undefined;
    readonly icon?: string | undefined;

    // To specify where the trip info data are stored e.g. which metrics definition they are stored in and what the name of the metrics are.
    // There are default values for these settings therefore, they are only needed for projects that have different set up.
    readonly tripsInfoDataSettings?: TripInfoDataSettings | undefined;

    // The style to apply to the container that displays the trips. This apply to only the element that contain the trips rows, not the
    // component
    readonly tripsContainerStyle?: CSSProperties | undefined;

    // To set the sort order of the trips. The default will be DESC (latest trips show first) if this is not provided.
    readonly sortOrder?: SortOrder;

    readonly durationFormat?: {
        // The time format to use e.g. "hh:mm"
        readonly format: string;

        // The user friendly display of the format
        readonly display: string;
    } | undefined;

    // By default, the component will display all fields, 'driverName', 'vehicleName', 'startTime', 'endTime', 'duration'. This option is for
    // hiding some columns.
    readonly hiddenColumns?: {
        readonly driverName?: boolean | undefined;
        readonly vehicleName?: boolean | undefined;
        readonly startTime?: boolean | undefined;
        readonly endTime?: boolean | undefined;
        readonly duration?: boolean | undefined;
    } | undefined;

    // The index of the view where the trips map are shown. When a trip is clicked, the component will take the user to the trips view/page
    // where the map of the trip is displayed. If this is not provided, clicking on a trip will not do anything
    // TODO - Improve this
    readonly tripsViewIndex?: number | undefined;
}



/**
 * type guard function to check if an object is a type of EntityTripsTableUIComponentCustomSettings
 */
const isEntityTripsTableUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityTripsTableUIComponentCustomSettings => {
    const settings = obj as EntityTripsTableUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be undefined or a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string.',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    // tripsInfoDataSettings is optional but if it is provided then it must have the right values
    if (
        settings.tripsInfoDataSettings !== undefined
        && !isTripInfoDataSettings(settings.tripsInfoDataSettings, errorLogger, [...configPath, 'tripsInfoDataSettings'])
    ) {
        return false;
    }

    if (settings.tripsContainerStyle && !isObjectLiteral(settings.tripsContainerStyle)) {
        return errorLogger?.logError({
            message: '"tripsContainerStyle" must be undefined or a CSSProperties Object',
            value  : settings.tripsContainerStyle,
            path   : [...configPath, 'tripsContainerStyle'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.sortOrder, false, false, Object.values(SortOrder))) {
        return errorLogger?.logError({
            message: `"sortOrder" must be undefined or one of the following values [${Object.values(SortOrder).map((value) => {
                return `"${value}"`;
            }).join(', ')}].`,
            value: settings.sortOrder,
            path : [...configPath, 'sortOrder'],
        }) ?? false;
    }


    // durationFormat is optional but if it is provided then it must have both format and display attributes
    if (settings.durationFormat !== undefined) {
        if (!isObjectLiteral(settings.durationFormat)) {
            return errorLogger?.logError({
                message: '"durationFormat" must be undefined or an object',
                value  : settings.durationFormat,
                path   : [...configPath, 'durationFormat'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.durationFormat.format, true)) {
            return errorLogger?.logError({
                message: '"format" is required and must be a non-empty string',
                value  : settings.durationFormat.format,
                path   : [...configPath, 'durationFormat', 'format'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.durationFormat.display, true)) {
            return errorLogger?.logError({
                message: '"display" is required and must be a non-empty string',
                value  : settings.durationFormat.display,
                path   : [...configPath, 'durationFormat', 'display'],
            }) ?? false;
        }
    }

    if (settings.hiddenColumns !== undefined) {
        if (!isObjectLiteral(settings.hiddenColumns)) {
            return errorLogger?.logError({
                message: '"hiddenColumns" must be undefined or an object.',
                value  : settings.hiddenColumns,
                path   : [...configPath, 'hiddenColumns'],
            }) ?? false;
        }

        if (!Object.entries(settings.hiddenColumns).every(([fieldName, value]) => {
            if (typeof value !== 'boolean') {
                return errorLogger?.logError({
                    message: 'Every values in "hiddenColumns" must be boolean',
                    value,
                    path   : [...configPath, 'hiddenColumns', fieldName],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    if (!isValidSettingNumberValue(settings.tripsViewIndex, false, false, 0, 100)) {
        return errorLogger?.logError({
            message: '"tripsViewIndex" must be undefined or an integer between 0 and 100',
            value  : settings.tripsViewIndex,
            path   : [...configPath, 'tripsViewIndex'],
        }) ?? false;
    }

    return true;
};



export interface EntityTripsTableUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityTripsTableUIComponentCustomSettings | undefined;
}


export const EntityTripsTableUIComponent: React.FC<EntityTripsTableUIComponentProps> = (props: EntityTripsTableUIComponentProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const panelClasses = useModePanelStyle();
    const tableClasses = useModeTableStyle();


    /**
     * Fetch data for ALL the entityClasses, the selected entity. Then find the entityClass that the selected entity belongs to and also
     * find the metrics definition from the entity class where we can fetch event data from.
     */
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);

    // Get the entity class that the selected entity belonged to
    const selectedEntityClass = useMemo(() => {
        return entityClasses?.find((entityClass) => {
            return entityClass.entityClass === selectedEntity?.entityClass;
        });
    }, [entityClasses, selectedEntity?.entityClass]);


    /**
     * To fetch location and events data, we will need to get the metrics definition where these data are stored. The info about the metrics
     * definitions are defined in the "customSettings" object. If these are not defined, use the default location and event metrics definitions.
     */
    const {
        tripEventMetricsDefinition, vehicleAssignmentEventMetricsDefinition,
    } = useMemo(() => {
        if (selectedEntityClass && props.customSettings) {

            // Get the ID of the metrics definition where trips info are stored. Use default ID if not provided.
            const tripsEventMetricsDefinitionId = props.customSettings?.tripsInfoDataSettings?.metricsDefinitionId ?? MOBILITY_DEFAULT_TRIP_EVENT_METRICS_DEFINITION_ID;
            const tripEventMetricsDefinition = selectedEntityClass.metricsDefinitions?.find((metricsDef) => {
                return metricsDef.metricsDefinitionId === tripsEventMetricsDefinitionId;
            });

            // To fetch trip info data, we need to use the "vehicle_assignment_event"s metricsDefinitionId therefore we need to the metricsDefinition
            // object for this metricsDefinition
            const vehicleAssignmentEventMetricsDefinitionId = props.customSettings?.tripsInfoDataSettings?.vehicleAssignmentEventMetricsDefinitionId
                                                                 ?? MOBILITY_DEFAULT_VEHICLE_ASSIGNMENT_EVENT_METRICS_DEFINITION_ID;
            const vehicleAssignmentEventMetricsDefinition = selectedEntityClass.metricsDefinitions?.find((metricsDef) => {
                return metricsDef.metricsDefinitionId === vehicleAssignmentEventMetricsDefinitionId;
            });


            // If we can't find some of the metricsDefinition, write some error messages in the console so that it is easier to debug the settings.
            if (!tripEventMetricsDefinition) {
                console.error(`Unable to find metrics definitions for '${tripsEventMetricsDefinitionId}'. Please check the customSettings or entity class definition`);
            }
            if (!vehicleAssignmentEventMetricsDefinition) {
                console.error(`Unable to find metrics definitions for '${vehicleAssignmentEventMetricsDefinitionId}'. Please check the customSettings or entity class definition`);
            }

            return {
                tripEventMetricsDefinition,
                vehicleAssignmentEventMetricsDefinition,
            };
        }

        return {
            tripEventMetricsDefinition             : undefined,
            vehicleAssignmentEventMetricsDefinition: undefined,
        };
    }, [selectedEntityClass, props.customSettings]);


    /**
     * Create the params that are required for fetching data based on the props.fetchDataParams. Since this component need to fetch data
     * based on some start/end time, it will convert the props.fetchDataParams to FetchDataByTimeRangeOptions.
     */
    const fetchDataTimeRange = useConvertTimeControlParamsToFetchDataTimeRangeParams(props.fetchDataParams);


    /**
     * Fetch the trips for the selectedEntity for the specified start/end time.
     */
    const tripsInfo = useFetchTripsInfoData(
        props.projectId,
        props.entityId,
        fetchDataTimeRange?.startTime,
        fetchDataTimeRange?.endTime,
        props.customSettings?.sortOrder,
        tripEventMetricsDefinition,
        vehicleAssignmentEventMetricsDefinition,
        props.customSettings?.tripsInfoDataSettings,
    );

    const entities = useFetchTripsVehicleAndDriverEntities(props.projectId, tripsInfo);


    const localizedTripsInfo = useMemo((): readonly LocalizedTripInfo[] | undefined => {
        return tripsInfo?.map((tripInfo): LocalizedTripInfo => {
            const vehicle = entities?.find((entity) => {
                return entity.entityId === tripInfo.vehicleEntityId;
            });
            const driver = entities?.find((entity) => {
                return entity.entityId === tripInfo.driverEntityId;
            });

            const durationFormat = props.customSettings?.durationFormat?.format ?? DEFAULT_DURATION_FORMAT.format;
            const displayDuration = moment.duration(tripInfo.duration, 'seconds').format(durationFormat);

            return {
                ...tripInfo,
                driverName      : driver?.displaySettings.name ?? tripInfo.driverEntityId,
                vehicleName     : vehicle?.displaySettings.name ?? tripInfo.vehicleEntityId,
                displayDuration,
                displayStartTime: getDateTimeDisplay(tripInfo.startTime, trans),
                displayEndTime  : getDateTimeDisplay(tripInfo.endTime, trans),
            };
        });
    }, [entities, props.customSettings?.durationFormat, trans, tripsInfo]);



    /**
     * We need to display the duration format in user friendly way. Therefore, show the actual format e.g. "hh:mm" might now work. We probably
     * want to show something like "Hrs: mins". Therefore, we need to get the duration format display value based on the settings.
     */
    const durationFormatDisplay = useMemo(() => {
        // If "durationFormat" is provided in the customSettings then use the "durationFormatDisplay" in the customSettings as well
        // because the DEFAULT_DURATION_FORMAT_DISPLAY probably doesn't match the custom "durationFormat"
        const formatDisplay = props.customSettings?.durationFormat?.display ?? DEFAULT_DURATION_FORMAT.display;

        return trans(formatDisplay);
    }, [props.customSettings?.durationFormat, trans]);


    const onTripClicked = useCallback((tripInfo: TripInfo) => {
        if (props.onShowEntityDetails && props.customSettings?.tripsViewIndex !== undefined) {
            props.onShowEntityDetails(props.entityId, props.customSettings.tripsViewIndex, tripInfo.startTime, tripInfo.endTime);
        }
    }, [props]);


    return (
        <>
            {selectedEntity && localizedTripsInfo && (
                <GenericPanelComp
                    className={clsx(props.className, classes.root)}
                    style={props.style}
                    title={props.customSettings?.titleKey ? trans(props.customSettings.titleKey) : undefined}
                    icon={props.customSettings?.icon}
                    headerless={!props.customSettings?.titleKey && !props.customSettings?.icon}
                >
                    <div
                        className={clsx(panelClasses.panelContent, classes.panelContent)}
                        style={props.customSettings?.tripsContainerStyle}
                    >
                        {localizedTripsInfo.length > 0 && (
                            <Table className={tableClasses.root}>

                                <TableHead className={tableClasses.tableHeader}>
                                    <TableRow className={tableClasses.tableHeaderRow}>
                                        {props.customSettings?.hiddenColumns?.driverName !== true && (
                                            <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'entity-name')}>
                                                {capitalizeAll(trans('mobility.label.driver'))}
                                            </TableCell>
                                        )}
                                        {props.customSettings?.hiddenColumns?.vehicleName !== true && (
                                            <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'entity-name')}>
                                                {capitalizeAll(trans('mobility.label.vehicle'))}
                                            </TableCell>
                                        )}
                                        {props.customSettings?.hiddenColumns?.startTime !== true && (
                                            <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'timestamp')}>
                                                {capitalizeAll(trans('mobility.label.start_time'))}
                                            </TableCell>
                                        )}
                                        {props.customSettings?.hiddenColumns?.endTime !== true && (
                                            <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'timestamp')}>
                                                {capitalizeAll(trans('mobility.label.end_time'))}
                                            </TableCell>
                                        )}
                                        {props.customSettings?.hiddenColumns?.duration !== true && (
                                            <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'duration')}>
                                                {capitalizeAll(trans('label.duration'))} ({durationFormatDisplay})
                                            </TableCell>
                                        )}
                                        <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'empty')} />
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {localizedTripsInfo.map((tripInfo) => {
                                        return (
                                            <TableRow
                                                key={`${`${tripInfo.driverEntityId}-${tripInfo.vehicleEntityId}-${tripInfo.startTime}`}`}
                                                className={clsx(tableClasses.tableBodyRow, classes.tableBodyRow)}
                                                onClick={() => {
                                                    onTripClicked(tripInfo);
                                                }}
                                            >
                                                {props.customSettings?.hiddenColumns?.driverName !== true && (
                                                    <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'entity-name')}>
                                                        {tripInfo.driverName}
                                                    </TableCell>
                                                )}
                                                {props.customSettings?.hiddenColumns?.vehicleName !== true && (
                                                    <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'entity-name')}>
                                                        {tripInfo.vehicleName}
                                                    </TableCell>
                                                )}
                                                {props.customSettings?.hiddenColumns?.startTime !== true && (
                                                    <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'timestamp')}>
                                                        {tripInfo.displayStartTime}
                                                    </TableCell>
                                                )}
                                                {props.customSettings?.hiddenColumns?.endTime !== true && (
                                                    <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'timestamp')}>
                                                        {tripInfo.displayEndTime}
                                                    </TableCell>
                                                )}
                                                {props.customSettings?.hiddenColumns?.duration !== true && (
                                                    <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'duration')}>
                                                        {tripInfo.displayDuration}
                                                    </TableCell>
                                                )}
                                                <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'empty')} />
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}

                        {localizedTripsInfo.length === 0 && (
                            <GenericMessageBox
                                title={trans('generic.no_data.title', {
                                    term: capitalizeAll(trans('mobility.label.trip', { count: 2 })),
                                })}
                            />
                        )}
                    </div>
                </GenericPanelComp>
            )}

            {!localizedTripsInfo && (
                <CircularProgress />
            )}
        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityTripsTableUIComponent', EntityTripsTableUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Trips_Table_Comp',
    {
        componentFunc          : EntityTripsTableUIComponent,
        customSettingsValidator: isEntityTripsTableUIComponentCustomSettings,
    },
);
